// src/components/Contact/Contact.jsx
import { Box, Typography, Container, TextField, Button, Grid, Paper, IconButton, Snackbar, Alert } from '@mui/material';
import { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import { motion } from 'framer-motion';
import { Email, LinkedIn, GitHub } from '@mui/icons-material';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  const subjectRef = useRef(null);
  const messageRef = useRef(null);
  const formRef = useRef(null);
  const sendButtonRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('Form submitted with data:', formData);
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setSnackbarMessage('Please fill in all required fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSnackbarMessage('Please enter a valid email address');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    // Prepare email data
    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      to_email: 'divyanshu99k@gmail.com',
      to_name: 'Divyanshu',
      subject: formData.subject || 'New Message from Portfolio Contact Form',
      message: formData.message,
      reply_to: formData.email
    };

    // Show loading state
    setSnackbarMessage('Sending message...');
    setSnackbarSeverity('info');
    setOpenSnackbar(true);

    // Send email using EmailJS
    console.log('Sending email with params:', templateParams);
    
    emailjs.send(
      'service_kk0i577', 
      'template_of98n0i', 
      templateParams,
      'F5eF9J9w6ETJTYjqa'
    )
    .then((response) => {
      console.log('Email sent successfully!', response.status, response.text);
      
      // Show success message
      setSnackbarMessage(
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ mr: 1.5, display: 'flex' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="#4CAF50"/>
            </svg>
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>Success!</Typography>
            <Typography variant="body2" sx={{ color: '#2E7D32' }}>Your message has been sent successfully!</Typography>
          </Box>
        </Box>
      );
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      
      // Only clear the form after successful send
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    })
    .catch((error) => {
      console.error('Failed to send email:', error);
      // Show error message but don't clear the form
      setSnackbarMessage('Failed to send message. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    });
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      subjectRef.current?.focus();
    }
  };

  const handleSubjectKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      messageRef.current?.focus();
    }
  };

  const handleMessageKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendButtonRef.current?.click();
    }
  };
  return (
    <Box id="contact" sx={{ py: 8, bgcolor: 'background.paper' }}>
      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiPaper-root': {
            minWidth: '300px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: '12px',
            padding: '12px 16px',
          },
          '& .MuiAlert-filledSuccess': {
            backgroundColor: '#E8F5E9',
            color: '#2E7D32',
            fontWeight: 500,
            boxShadow: 'none',
            padding: '16px',
            borderRadius: '12px',
          },
          '& .MuiAlert-icon': {
            color: '#4CAF50',
            marginRight: '12px',
            padding: 0,
            fontSize: '24px',
          },
          '& .MuiAlert-message': {
            padding: 0,
          },
        }}
      >
        {typeof snackbarMessage === 'string' ? (
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbarSeverity} 
            sx={{ 
              width: '100%',
              '& .MuiAlert-message': {
                display: 'flex',
                alignItems: 'center',
              }
            }}
          >
            {snackbarMessage}
          </Alert>
        ) : (
          <Box
            sx={{
              backgroundColor: '#E8F5E9',
              color: '#2E7D32',
              padding: '16px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box sx={{ mr: 1.5, display: 'flex' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="#4CAF50"/>
              </svg>
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>Success!</Typography>
              <Typography variant="body2" sx={{ color: '#2E7D32' }}>Your message has been sent successfully!</Typography>
            </Box>
          </Box>
        )}
      </Snackbar>
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h2" component="h2" align="center" sx={{ mb: 6 }}>
            Get In Touch
          </Typography>
          
          <Paper 
            elevation={3} 
            sx={{ 
              p: { xs: 3, md: 6 },
              borderRadius: 2,
              bgcolor: 'background.default',
            }}
          >
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Contact Information
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1.5, bgcolor: 'rgba(0, 188, 212, 0.05)', borderRadius: 1 }}>
                      <Email color="primary" />
                      <Typography>divyanshu99k@gmail.com</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<LinkedIn />}
                      href="https://www.linkedin.com/in/divyanshu27k/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                          bgcolor: 'rgba(0, 188, 212, 0.1)',
                          borderColor: 'primary.dark',
                        },
                      }}
                    >
                      LinkedIn Profile
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<GitHub />}
                      href="https://github.com/Divyanshu27k"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                          bgcolor: 'rgba(0, 188, 212, 0.1)',
                          borderColor: 'primary.dark',
                        },
                      }}
                    >
                      GitHub Profile
                    </Button>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <form ref={formRef} onSubmit={handleSubmit}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                        <TextField
                          fullWidth
                          name="name"
                          label="Your Name"
                          variant="outlined"
                          required
                          size="medium"
                          value={formData.name}
                          onChange={handleInputChange}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '1.1rem',
                              height: '56px',
                              '& fieldset': {
                                borderRadius: '12px',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '1rem',
                              top: '-6px'
                            }
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                        <TextField
                          fullWidth
                          name="email"
                          label="Your Email"
                          type="email"
                          variant="outlined"
                          required
                          size="medium"
                          value={formData.email}
                          onChange={handleInputChange}
                          onKeyDown={handleEmailKeyDown}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '1.1rem',
                              height: '56px',
                              '& fieldset': {
                                borderRadius: '12px',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '1rem',
                              top: '-6px'
                            }
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 100%' }}>
                        <TextField
                          fullWidth
                          name="subject"
                          label="Subject"
                          variant="outlined"
                          size="medium"
                          value={formData.subject}
                          onChange={handleInputChange}
                          inputRef={subjectRef}
                          onKeyDown={handleSubjectKeyDown}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '1.1rem',
                              height: '56px',
                              '& fieldset': {
                                borderRadius: '12px',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '1rem',
                              top: '-6px'
                            }
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 100%' }}>
                        <TextField
                          fullWidth
                          name="message"
                          label="Your Message"
                          multiline
                          rows={6}
                          variant="outlined"
                          required
                          size="medium"
                          value={formData.message}
                          onChange={handleInputChange}
                          inputRef={messageRef}
                          onKeyDown={handleMessageKeyDown}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '1.1rem',
                              minHeight: '180px',
                              alignItems: 'flex-start',
                              paddingTop: '14px',
                              '& fieldset': {
                                borderRadius: '12px',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '1rem',
                              top: '-6px'
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    <Button
                      ref={sendButtonRef}
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      sx={{ 
                        mt: 2,
                        py: 2,
                        fontSize: '1.1rem',
                        fontWeight: 500,
                        height: '56px',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                        },
                        transition: 'all 0.2s',
                      }}
                    >
                      Send Message
                    </Button>
                  </Box>
                </form>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Contact;